/**
 * Cierre: las reglas de oro aparecen mientras la voz las dice (overlay
 * `rules`) y, al terminar, los botones para volver a ver, ir al Vault o salir.
 */
import { KeyRound, RotateCcw, X } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import type { Workshop } from './types';

interface ClosingCardProps {
  rules: Workshop['rules'];
  show: boolean;
  ended: boolean;
  onReplay: () => void;
  onGoVault: () => void;
  onExit: () => void;
}

export function ClosingCard({ rules, show, ended, onReplay, onGoVault, onExit }: ClosingCardProps) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className={`fixed inset-0 z-204 flex items-center justify-center bg-[#050d0c]/70 px-4 backdrop-blur-md ${ended ? '' : 'pointer-events-none'}`}
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
        >
          <motion.div
            className="w-full max-w-2xl rounded-3xl border border-white/10 bg-[#0f1a18] p-7 text-white shadow-2xl md:p-9"
            initial={reduce ? false : { y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-green">{t('vault.workshop.rules_eyebrow', 'Reglas de oro')}</div>
            <ol className="mt-5 space-y-3">
              {rules.map((r, i) => (
                <motion.li
                  key={r.title}
                  className="flex gap-4 rounded-2xl border border-white/8 bg-white/4 px-4 py-3"
                  initial={reduce ? false : { opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: reduce ? 0 : 0.5 + i * 1.9, duration: 0.35 }}
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-green text-sm font-bold text-[#06110f]">{i + 1}</span>
                  <div>
                    <div className="font-semibold">{r.title}</div>
                    <div className="text-sm text-white/65">{r.body}</div>
                  </div>
                </motion.li>
              ))}
            </ol>
            <AnimatePresence>
              {ended && (
                <motion.div className="mt-7 flex flex-wrap items-center gap-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <button type="button" onClick={onGoVault} className="inline-flex items-center gap-2 rounded-xl bg-brand-green px-4 py-2.5 text-sm font-semibold text-[#06110f] transition-colors hover:bg-[#3fd8ca]">
                    <KeyRound className="h-4 w-4" />
                    {t('vault.workshop.go_vault', 'Ir al Vault')}
                  </button>
                  <button type="button" onClick={onReplay} className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 text-sm text-white/90 transition-colors hover:bg-white/10">
                    <RotateCcw className="h-4 w-4" />
                    {t('vault.workshop.replay', 'Volver a ver')}
                  </button>
                  <button type="button" onClick={onExit} className="ml-auto inline-flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/10 hover:text-white">
                    <X className="h-4 w-4" />
                    {t('vault.workshop.close', 'Salir')}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
